import { useState } from 'react';
import type { SupportedLanguage } from '../types';

// Localized tooltips for language suggestion
const LOCALIZED_TOOLTIPS: Record<string, { switchTo: string; selectLanguage: string }> = {
  en: { switchTo: 'Switch to', selectLanguage: 'Select language' },
  de: { switchTo: 'Wechseln zu', selectLanguage: 'Sprache wählen' },
  cs: { switchTo: 'Přepnout na', selectLanguage: 'Vybrat jazyk' },
  fr: { switchTo: 'Passer à', selectLanguage: 'Choisir la langue' },
  es: { switchTo: 'Cambiar a', selectLanguage: 'Seleccionar idioma' },
  it: { switchTo: 'Passa a', selectLanguage: 'Seleziona lingua' },
  pl: { switchTo: 'Przełącz na', selectLanguage: 'Wybierz język' },
  sk: { switchTo: 'Prepnúť na', selectLanguage: 'Vybrať jazyk' },
};

interface LanguageEarProps {
  currentLanguage: string;
  cfLanguage: string | null;
  showCfSuggestion: boolean;
  supportedLanguages: SupportedLanguage[];
  onSelectLanguage: (code: string) => void;
  onDismissSuggestion: () => void;
}

export function LanguageEar({
  currentLanguage,
  cfLanguage,
  showCfSuggestion,
  supportedLanguages,
  onSelectLanguage,
  onDismissSuggestion,
}: LanguageEarProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Find current language details
  const currentLang = supportedLanguages.find(l => l.code === currentLanguage);
  const cfLang = cfLanguage ? supportedLanguages.find(l => l.code === cfLanguage) : null;

  // When CF suggests a different language, show THAT flag to draw attention
  // Otherwise show the current/selected language flag
  const hasSuggestion = showCfSuggestion && cfLang && cfLanguage !== currentLanguage;
  const earFlag = hasSuggestion ? cfLang.flag : (currentLang?.flag || '🌐');

  // Get localized strings - use CF language for suggestion, current language for select
  const getSuggestionTooltip = () => {
    if (!cfLang || !cfLanguage) return '';
    const strings = LOCALIZED_TOOLTIPS[cfLanguage] || LOCALIZED_TOOLTIPS['en'];
    return `${strings.switchTo} ${cfLang.name}?`;
  };

  const getSelectTooltip = () => {
    const strings = LOCALIZED_TOOLTIPS[currentLanguage] || LOCALIZED_TOOLTIPS['en'];
    return strings.selectLanguage;
  };

  const handleLanguageClick = (code: string) => {
    onSelectLanguage(code);
    onDismissSuggestion(); // Dismiss suggestion when user makes explicit choice
    setIsOpen(false);
  };

  const handleCfClick = () => {
    if (cfLanguage) {
      onSelectLanguage(cfLanguage);
      onDismissSuggestion();
    }
  };

  const handleEarClick = () => {
    if (hasSuggestion) {
      handleCfClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const tooltip = hasSuggestion ? getSuggestionTooltip() : getSelectTooltip();

  return (
    <>
      {/* Small Ear - positioned above contact drawer ear, matching its design */}
      <button
        onClick={handleEarClick}
        className={`
          absolute right-0 bottom-32 z-40
          w-6 h-8 rounded-l-md
          bg-white shadow-md border border-r-0 border-gray-200
          flex items-center justify-center
          text-sm cursor-pointer
          transition-all duration-200
          hover:w-7 hover:shadow-lg
          ${hasSuggestion ? 'ring-2 ring-blue-400 ring-offset-1 animate-pulse' : ''}
        `}
        aria-label={tooltip}
        title={tooltip}
      >
        {earFlag}
      </button>

      {/* Compact Dropdown Panel - appears above the ear */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="absolute inset-0 z-35"
            onClick={() => setIsOpen(false)}
          />
          {/* Language options */}
          <div className="absolute right-0 bottom-[8.5rem] z-40 w-36 bg-white rounded-l-lg shadow-xl border border-gray-200 overflow-hidden">
            {supportedLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageClick(lang.code)}
                className={`
                  w-full flex items-center gap-2 px-2 py-1.5
                  text-xs transition-colors duration-100
                  ${lang.code === currentLanguage
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-gray-50 text-gray-700'}
                `}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="flex-1 text-left">{lang.name}</span>
                {lang.code === currentLanguage && (
                  <span className="text-blue-600 text-xs">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
